import {gatAvailability} from '~src/utils/availability'

/**
 * Get availability by serviceId instead of serviceName
 * This is crucial for distinguishing between room types with same names but different sustainability levels
 */
function getAvailabilityByServiceId(
    availabilityType: any[],
    date: Date,
    allotmentType: string,
    serviceId: any,
    subAllotment: any = null
) {
    const availabilities = availabilityType.filter(
        availability =>
            new Date(`${availability.dateFrom}T00:00`) <= date &&
            date <= new Date(`${availability.dateTo}T23:59`) &&
            availability.allotmentType === allotmentType &&
            availability.serviceId === serviceId &&
            (!subAllotment || availability.subAllotmentId === subAllotment.id)
    )

    if (!availabilities.length) {
        console.warn(
            `No availability found for serviceId: ${serviceId}, allotmentType: ${allotmentType}, date: ${date}`
        )
        return null
    }

    // Use the same aggregation logic as gatAvailability
    return availabilities.reduce(
        (totalAvailability, availability) => {
            if (!availability.canBeUndefined || !totalAvailability.availableQuantity) {
                totalAvailability.allocatedQuantity += availability.allocatedQuantity
                totalAvailability.availableQuantity += availability.availableQuantity
                totalAvailability.bookedQuantity += availability.bookedQuantity
            }
            totalAvailability.release = availability.release
            totalAvailability.status =
                availabilities.length === 1
                    ? availability.status
                    : availabilities.every(({status}) => status === 'UNLIMITED')
                      ? 'UNLIMITED'
                      : availabilities.every(({status}) => status === 'RELEASED')
                        ? 'RELEASED'
                        : availabilities.find(
                                ({status, availableQuantity}) =>
                                    ['AVAILABLE', 'OVERBOOKING'].includes(status) && availableQuantity
                            )
                          ? 'AVAILABLE'
                          : availabilities.every(({status}) => status === 'BLOCKED')
                            ? 'BLOCKED'
                            : availabilities.every(({status}) => status === 'TERMINATED_ALLOTMENT')
                              ? 'TERMINATED_ALLOTMENT'
                              : null
            totalAvailability.canBeUndefined = availabilities.length === 1 && availability.canBeUndefined
            return totalAvailability
        },
        {
            allocatedQuantity: 0,
            availableQuantity: 0,
            bookedQuantity: 0,
            status: null,
            release: 0,
            canBeUndefined: false,
        }
    )
}

export interface ZSHAvailabilityResult {
    allocatedQuantity: number
    availableQuantity: number
    bookedQuantity: number
    status: string | null
    release: number
    canBeUndefined: boolean
    // ZSH-specific fields
    initialAvailability?: number
    displayFormat?: 'normal' | 'constrained' // normal: "5", constrained: "3 (5)"
    isConstrainedByBasic?: boolean
}

export interface RoomGroup {
    basicRoomId: string | number
    basicRoomName: string
    basicRoom: any
    sustainableRoom: any
    zeroRoom: any
}

/**
 * Calculate ZSH availability for a specific room type within a room group
 * Implements the requirements from room-availability-logic-requirements.txt
 */
export function getZSHAvailability(
    availabilityType: any[],
    date: Date,
    allotmentType: string,
    roomGroup: RoomGroup,
    roomType: 'basic' | 'sustainable' | 'zero',
    subAllotment: any = null
): ZSHAvailabilityResult | null {
    console.log(`=== getZSHAvailability for ${roomType} ===`)
    console.log('Room group:', {
        basicRoomId: roomGroup.basicRoomId,
        basicServiceId: roomGroup.basicRoom?.serviceId,
        sustainableServiceId: roomGroup.sustainableRoom?.serviceId,
        zeroServiceId: roomGroup.zeroRoom?.serviceId,
    })

    // Get base availability for each room type in the group using serviceId for precise matching
    const basicAvailability = roomGroup.basicRoom
        ? getAvailabilityByServiceId(availabilityType, date, allotmentType, roomGroup.basicRoom.serviceId, subAllotment)
        : null

    const sustainableAvailability = roomGroup.sustainableRoom
        ? getAvailabilityByServiceId(
              availabilityType,
              date,
              allotmentType,
              roomGroup.sustainableRoom.serviceId,
              subAllotment
          )
        : null

    const zeroAvailability = roomGroup.zeroRoom
        ? getAvailabilityByServiceId(availabilityType, date, allotmentType, roomGroup.zeroRoom.serviceId, subAllotment)
        : null

    console.log('Availability results:', {
        basic: basicAvailability?.availableQuantity,
        sustainable: sustainableAvailability?.availableQuantity,
        zero: zeroAvailability?.availableQuantity,
    })

    // Get the requested room's base availability
    let targetAvailability: any = null
    switch (roomType) {
        case 'basic':
            targetAvailability = basicAvailability
            break
        case 'sustainable':
            targetAvailability = sustainableAvailability
            break
        case 'zero':
            targetAvailability = zeroAvailability
            break
    }

    if (!targetAvailability) {
        return null
    }

    // Create the result object with base values
    const result: ZSHAvailabilityResult = {
        allocatedQuantity: targetAvailability.allocatedQuantity,
        availableQuantity: targetAvailability.availableQuantity,
        bookedQuantity: targetAvailability.bookedQuantity,
        status: targetAvailability.status,
        release: targetAvailability.release,
        canBeUndefined: targetAvailability.canBeUndefined,
        initialAvailability: targetAvailability.availableQuantity,
        displayFormat: 'normal',
        isConstrainedByBasic: false,
    }

    // Apply ZSH availability rules for special rooms
    if (roomType === 'sustainable' || roomType === 'zero') {
        applySpecialRoomRules(result, basicAvailability, targetAvailability)
    }

    return result
}

/**
 * Apply special room availability rules according to requirements
 */
function applySpecialRoomRules(result: ZSHAvailabilityResult, basicAvailability: any, specialAvailability: any): void {
    if (!basicAvailability || !specialAvailability) {
        return
    }

    const basicAvailable = basicAvailability.availableQuantity || 0
    const specialAvailable = specialAvailability.availableQuantity || 0

    // Store the initial (unconstrained) availability
    result.initialAvailability = specialAvailable

    // Rule: if Basic_Availability < Special_Availability:
    // Display Special_Availability as: Basic_Availability (Initial_Special_Availability)
    if (basicAvailable < specialAvailable) {
        result.availableQuantity = basicAvailable
        result.displayFormat = 'constrained'
        result.isConstrainedByBasic = true

        // Update status based on the constraining basic room
        if (basicAvailable <= 0) {
            // If basic room has no availability, special room should show as unavailable
            result.status = basicAvailability.status
        }
    }
}

/**
 * Calculate availability considering cross-room constraints within a group
 * This handles the complex scenarios described in the requirements
 */
export function getZSHAvailabilityWithConstraints(
    availabilityType: any[],
    date: Date,
    allotmentType: string,
    roomGroup: RoomGroup,
    roomType: 'basic' | 'sustainable' | 'zero',
    subAllotment: any = null
): ZSHAvailabilityResult | null {
    // Get base availability for all room types in the group using serviceId
    const basicAvailability = roomGroup.basicRoom
        ? getAvailabilityByServiceId(availabilityType, date, allotmentType, roomGroup.basicRoom.serviceId, subAllotment)
        : null

    const sustainableAvailability = roomGroup.sustainableRoom
        ? getAvailabilityByServiceId(
              availabilityType,
              date,
              allotmentType,
              roomGroup.sustainableRoom.serviceId,
              subAllotment
          )
        : null

    const zeroAvailability = roomGroup.zeroRoom
        ? getAvailabilityByServiceId(availabilityType, date, allotmentType, roomGroup.zeroRoom.serviceId, subAllotment)
        : null

    // Apply cross-room constraints
    return applyGroupConstraints(roomType, basicAvailability, sustainableAvailability, zeroAvailability)
}

/**
 * Apply constraints between all room types in a group
 */
function applyGroupConstraints(
    targetRoomType: 'basic' | 'sustainable' | 'zero',
    basicAvailability: any,
    sustainableAvailability: any,
    zeroAvailability: any
): ZSHAvailabilityResult | null {
    let targetAvailability: any = null

    switch (targetRoomType) {
        case 'basic':
            targetAvailability = basicAvailability
            break
        case 'sustainable':
            targetAvailability = sustainableAvailability
            break
        case 'zero':
            targetAvailability = zeroAvailability
            break
    }

    if (!targetAvailability) {
        return null
    }

    const result: ZSHAvailabilityResult = {
        allocatedQuantity: targetAvailability.allocatedQuantity,
        availableQuantity: targetAvailability.availableQuantity,
        bookedQuantity: targetAvailability.bookedQuantity,
        status: targetAvailability.status,
        release: targetAvailability.release,
        canBeUndefined: targetAvailability.canBeUndefined,
        initialAvailability: targetAvailability.availableQuantity,
        displayFormat: 'normal',
        isConstrainedByBasic: false,
    }

    // Apply constraints for special rooms
    if (targetRoomType === 'sustainable' || targetRoomType === 'zero') {
        if (basicAvailability) {
            applySpecialRoomRules(result, basicAvailability, targetAvailability)
        }
    }

    return result
}

/**
 * Get availability for basic room in a group
 * This shows the basic room's own availability
 */
export function getBasicRoomGroupAvailability(
    availabilityType: any[],
    date: Date,
    allotmentType: string,
    roomGroup: RoomGroup,
    subAllotment: any = null
): ZSHAvailabilityResult | null {
    console.log('=== getBasicRoomGroupAvailability ===')
    console.log('Basic room service ID:', roomGroup.basicRoom?.serviceId)

    if (roomGroup.basicRoom) {
        // Basic room exists, show its availability
        return getZSHAvailability(availabilityType, date, allotmentType, roomGroup, 'basic', subAllotment)
    } else {
        // No basic room, show aggregated availability for the group
        // This handles cases where only sustainable/zero rooms exist
        return getAggregatedGroupAvailability(availabilityType, date, allotmentType, roomGroup, subAllotment)
    }
}

/**
 * Get aggregated availability when no basic room exists in the group
 */
function getAggregatedGroupAvailability(
    availabilityType: any[],
    date: Date,
    allotmentType: string,
    roomGroup: RoomGroup,
    subAllotment: any = null
): ZSHAvailabilityResult | null {
    // Get availability for all rooms in the group
    const availabilities = []

    if (roomGroup.sustainableRoom) {
        const avail = getAvailabilityByServiceId(
            availabilityType,
            date,
            allotmentType,
            roomGroup.sustainableRoom.serviceId,
            subAllotment
        )
        if (avail) availabilities.push(avail)
    }

    if (roomGroup.zeroRoom) {
        const avail = getAvailabilityByServiceId(
            availabilityType,
            date,
            allotmentType,
            roomGroup.zeroRoom.serviceId,
            subAllotment
        )
        if (avail) availabilities.push(avail)
    }

    if (availabilities.length === 0) {
        return null
    }

    // Aggregate the availabilities
    const aggregated = availabilities.reduce(
        (total, avail) => ({
            allocatedQuantity: total.allocatedQuantity + avail.allocatedQuantity,
            availableQuantity: total.availableQuantity + avail.availableQuantity,
            bookedQuantity: total.bookedQuantity + avail.bookedQuantity,
            status: total.status || avail.status,
            release: Math.max(total.release, avail.release),
            canBeUndefined: total.canBeUndefined || avail.canBeUndefined,
        }),
        {
            allocatedQuantity: 0,
            availableQuantity: 0,
            bookedQuantity: 0,
            status: null,
            release: 0,
            canBeUndefined: false,
        }
    )

    return {
        ...aggregated,
        initialAvailability: aggregated.availableQuantity,
        displayFormat: 'normal' as const,
        isConstrainedByBasic: false,
    }
}
